import React, { FC, useEffect, useState } from 'react';
import { JobListItem } from '../jobs.model';
import { getSortedJobTags } from '../utils';
import MoreTagsPopover from './MoreTagsPopover';
import TableTagPill from './TableTagPill';

interface DynamicTagsDisplayProps {
  job: JobListItem;
}

export const TAGS_CONTAINER_HEIGHT = 102;

const DynamicTagsDisplay: FC<DynamicTagsDisplayProps> = ({ job }) => {
  const [visibleTagsCount, setVisibleTagsCount] = useState(2);
  const [isCalculated, setIsCalculated] = useState(false);

  useEffect(() => {
    if (!job.jobTags?.length) {
      setIsCalculated(true);
      return;
    }

    const calculateVisibleTags = () => {
      const sortedTags = getSortedJobTags(job.jobTags);

      if (sortedTags.length <= 2) {
        setVisibleTagsCount(sortedTags.length);
        setIsCalculated(true);
        return;
      }

      // Estimate tag dimensions based on typical tag content
      const estimateTagWidth = (tag: { name: string; icon?: string }) => {
        // Base padding: 6px left + 6px right = 12px
        // Icon width: ~13px + 6px gap = 19px (if icon exists)
        // Text width: ~6px per character (rough estimate for 13px font)
        const basePadding = 12;
        const iconWidth = tag.icon ? 19 : 0;
        const textWidth = (tag.name || '').length * 6;
        const borderWidth = 2; // 1px border on each side

        return Math.min(basePadding + iconWidth + textWidth + borderWidth, 120); // Max width constraint
      };

      const containerWidth = 146;
      const containerHeight = TAGS_CONTAINER_HEIGHT;
      const gap = 4;
      const estimatedTagHeight = 26; // Approximate height based on padding and font size
      const maxRowsInContainer = Math.floor(containerHeight / (estimatedTagHeight + gap));

      // Estimate "more" button width: "+X more" where X can be 1-2 digits
      const moreButtonWidth = 45;

      let currentRowWidth = 0;
      let currentRow = 0;
      let visibleCount = 0;

      for (let i = 0; i < sortedTags.length; i++) {
        const tag = sortedTags[i];
        const tagWidth = estimateTagWidth(tag);

        const isLastPossibleTag = i === sortedTags.length - 1;
        const needsMoreButton = !isLastPossibleTag;
        const requiredWidth = needsMoreButton ? tagWidth + gap + moreButtonWidth : tagWidth;

        if (currentRowWidth + requiredWidth <= containerWidth) {
          currentRowWidth += tagWidth + gap;
          visibleCount = i + 1;
        } else {
          currentRow++;

          if (currentRow >= maxRowsInContainer) {
            break;
          }

          currentRowWidth = tagWidth + gap;
          visibleCount = i + 1;
        }
      }

      const finalCount = Math.max(1, Math.min(visibleCount, sortedTags.length));
      setVisibleTagsCount(finalCount);
      setIsCalculated(true);
    };

    calculateVisibleTags();
  }, [job.jobTags]);

  const sortedTags = getSortedJobTags(job.jobTags || []);
  const hasMoreTags = sortedTags.length > visibleTagsCount;

  if (!job.jobTags?.length) {
    return <div className="flex h-full flex-wrap gap-1">-</div>;
  }

  if (!isCalculated) {
    return (
      <div className="flex h-full flex-wrap gap-1">
        {sortedTags.slice(0, 2).map(tag => (
          <TableTagPill key={tag.id} tag={tag} />
        ))}
        {sortedTags.length > 2 && <MoreTagsPopover job={job} />}
      </div>
    );
  }

  return (
    <div className="flex h-fit flex-wrap gap-1">
      {sortedTags.slice(0, visibleTagsCount).map((tag, index) => (
        <TableTagPill key={tag.id} tag={tag} id={`tag-${index + 1}`} />
      ))}
      {hasMoreTags && (
        <div className="self-center">
          <MoreTagsPopover job={job} visibleCount={visibleTagsCount} />
        </div>
      )}
    </div>
  );
};

export default DynamicTagsDisplay;
